"use client"

import { Mic, Volume2 } from "lucide-react"
import type React from "react"

import { useState, useEffect } from "react"

interface InterviewModeToggleProps {
  isAuto: boolean
  setIsAuto: (value: boolean | ((prev: boolean) => boolean)) => void
  theme?: "light" | "dark"
  disabled?: boolean
}

export default function InterviewModeToggle({ isAuto, setIsAuto,disabled, theme = "light" }: InterviewModeToggleProps) {
  const [showHint, setShowHint] = useState(false)
  const [hintMessage, setHintMessage] = useState("")

  useEffect(() => {
    if (showHint) {
      const timer = setTimeout(() => {
        setShowHint(false)
      }, 3000) // Hide after 3 seconds

      return () => clearTimeout(timer)
    }
  }, [showHint])

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const newMode = !isAuto
    setIsAuto(newMode)

    // Set the appropriate hint message
    if (newMode) {
      setHintMessage("Hands-free lets you speak freely.")
    } else {
      setHintMessage("Push to Talk requires pressing the mic button to speak.")
    }

    setShowHint(true)
  }

  return (
    <div className="flex flex-col items-start relative">
      <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">Conversation Mode</span>
      <div className="w-[400px] flex justify-start">
        <button
          className={`relative text-xs md:text-xs  w-[118px] disabled:opacity-50 md:w-32 text-center disabled:cursor-not-allowed py-1.5 md:py-2 mt-1 flex items-center justify-center rounded-full  transition-colors duration-200 ${!isAuto
              ? "bg-accent-500 pr-4  md:pr-4 text-white"
              : theme === "dark"
                ? "bg-[rgba(40,39,44,1)] pl-4 text-slate-300"
                : "bg-[rgba(200,200,200,1)] pl-4 text-slate-700"
            }`}
          disabled={disabled}
          onClick={handleToggle}
        >
          <div className="flex items-center gap-1.5">
            {isAuto ? (
              <>
                <Volume2 size={14} />
                <span>Hands-free</span>
              </>
            ) : (
              <>
                <Mic size={14} />
                <span>Push to Talk</span>
              </>
            )}
          </div>
          <span
            className={`absolute left-2 top-1.5 md:top-1.5 w-4 h-4 md:w-5 md:h-5 bg-white rounded-full transition-transform duration-300 ${!isAuto ? " translate-x-20 md:ml-0 ml-2 md:translate-x-24" : "-translate-x-1"
              }`}
          />
        </button>
      </div>

      {/* Animated Hint Message */}
      <div
        className={`absolute top-16 left-0 transition-all duration-300 ease-in-out ${showHint ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"
          }`}
      >
        <div
          className={`px-3 py-2 rounded-lg shadow-lg text-sm font-medium ${theme === "dark"
              ? "bg-slate-800 text-slate-200 border border-slate-700"
              : "bg-white text-slate-700 border border-slate-200"
            }`}
        >
          {hintMessage}
          {/* Small arrow pointing up */}
          <div
            className={`absolute -top-1 left-4 w-2 h-2 rotate-45 ${theme === "dark"
                ? "bg-slate-800 border-l border-t border-slate-700"
                : "bg-white border-l border-t border-slate-200"
              }`}
          />
        </div>
      </div>
    </div>
  )
}
